import React, { useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ClearTwoToneIcon from "@mui/icons-material/ClearTwoTone";
import LanguageIcon from "@mui/icons-material/Language";
import { Box, Chip, CircularProgress, IconButton, Input, InputAdornment, Stack, Typography } from "@mui/material";
import { SendTo } from "assets/material-custom-icons";
import { FilesPickerModal, FilesPickerMode } from "components/modals/Files-picker/FilesPicker";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionType } from "constants/documents";
import { useActionHook } from "hooks";
import { useAllFilesSelect, useDealIdSelector, useDocumentsActions } from "store";
import {
  DocumentSection,
  DocumentSectionText,
  IAskAiQueryType,
  ICreateDocumentAIAnswerPayload,
  IFile
} from "store/types";

import { AiSkeletonLoader } from "../AI-skeleton-loader/AISkeletonLoader";
import { useContentContext } from "../Content/content-context";

interface ChatInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  section: DocumentSectionText | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onCreateSection?: (data: {
    question?: string;
    text?: string;
    position?: number;
  }) => Promise<DocumentSection | undefined>;
  startTyping: (text: string) => void;
  isTyping: boolean;
  documentDealId: number | null;
  getAIOptionAnswer: (data: ICreateDocumentAIAnswerPayload) => Promise<DocumentSection>;
  isChatAiAnswerLoading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  open,
  setOpen,
  section,
  inputRef,
  onCreateSection,
  startTyping,
  isTyping,
  documentDealId,
  getAIOptionAnswer,
  isChatAiAnswerLoading
}) => {
  const { generateAiDocumentAnswerAction } = useDocumentsActions();
  const { updateSection, addSectionToOpenChatInput } = useContentContext();
  const currentDealId = useDealIdSelector();
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<IAskAiQueryType | null>(null);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [dbFiles, setDbFiles] = useState<IFile[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const filesSubmittedRef = useRef(false);
  const files = useAllFilesSelect();

  const {
    action: getAiAnswer,
    loading,
    data: AiAnswer = null,
    setData
  } = useActionHook(generateAiDocumentAnswerAction, {
    onSuccess: (res) => {
      setInputValue("");
      onSubmitAnswer(res.answer, res.question);
    }
  });

  const { action: submitAiAnswerToSection } = useActionHook(updateSection, {
    onSuccess: (res) => {
      res.text && startTyping(res.text);
    }
  });

  const onSubmitAnswer = (answer: string, question: string) => {
    if (!section) return;
    if (!answer && !question) return;

    if (onCreateSection && section?.text && section?.parameters?.question) {
      onCreateSection({ question, text: answer });
      onCloseAIBox();
      return;
    }
    submitAiAnswerToSection({
      id: section.id,
      text: answer,
      parameters: { ...section.parameters, question }
    });
  };

  const onGetAISuggestion = async () => {
    if (!inputValue.length || loading || isTyping || !section?.document_id) return;

    if (!selectedOption) {
      return getAiAnswer(inputValue, section?.document_id);
    } else {
      if (selectedOption && section) {
        //before get AI answer, we need to create a new section in case to create "thinking" behavior and open new ChatInput in case user can continue working
        // Create a new TextSection next to the current one
        // Use onCreateSection if available (it handles position automatically),
        // otherwise use createSection from context
        let newSection;
        if (onCreateSection) {
          newSection = await onCreateSection({
            question: "",
            text: ""
          });
        }

        // If new section was created, mark it to open ChatInput
        if (newSection && newSection.type === DocumentSectionType.Text) {
          addSectionToOpenChatInput(newSection.id);
        }
      }
      getAIOptionAnswer({
        documentId: section?.document_id,
        sectionId: section?.id,
        send_ai: false,
        query_type: selectedOption,
        user_prompt: inputValue,
        ...(selectedOption === "ai_questions" && {
          file_ids: dbFiles.map((file) => file.id),
          files: localFiles
        })
      });
      onCloseAIBox();
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (loading) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onGetAISuggestion();
      return;
    }
    if (event.key === "Escape") {
      if (AiAnswer) {
        onCloseAIBox();
        return;
      }
      setOpen(false);
      setInputValue("");
    }
  };

  const onCloseAIBox = () => {
    setOpen(false);
    setInputValue("");
    setData(undefined);
  };

  const handleSelectFileForMe = () => {
    setDbFiles([]);
    setLocalFiles([]);
    setSelectedOption((prev) => (prev === "file_query" ? null : "file_query"));
  };

  const agentOptions = [
    {
      icon: <LanguageIcon sx={{ fill: "#1C1B1F", width: "18px", height: "18px" }} />,
      label: "Search The Web",
      value: "web_search"
    },
    {
      icon: <AddIcon sx={{ width: "20px", height: "20px" }} />,
      label: "Add Files",
      value: "ai_questions"
    }
  ];

  const handleOptionClick = (value: IAskAiQueryType) => {
    if (selectedOption === "ai_questions" && value !== "ai_questions" && (!!dbFiles.length || !!localFiles.length)) {
      return;
    }

    if (value === "ai_questions" && selectedOption === "file_query") {
      setIsFilePickerOpen(true);
      return;
    }

    if (selectedOption === value && value === "web_search") {
      setSelectedOption(null);
    } else {
      setSelectedOption(value);
    }

    if (value !== "ai_questions") {
      setDbFiles([]);
      setLocalFiles([]);
    }

    if (value === "ai_questions") {
      setIsFilePickerOpen(true);
    }
  };

  const handleFilePickerSubmit = async (data: { filesIds?: number[]; attachments: File[]; files?: IFile[] }) => {
    const hasFiles = !!(data.files?.length || data.filesIds?.length || data.attachments?.length);
    filesSubmittedRef.current = hasFiles;

    if (data.files || data.filesIds) {
      const filesFromStore = files.filter((file) => data.filesIds?.includes(file.id));
      setDbFiles(data.files || filesFromStore);
    }
    if (data.attachments) {
      setLocalFiles(data.attachments);
    }
  };

  const onCloseFilePicker = () => {
    setIsFilePickerOpen(false);

    if (!filesSubmittedRef.current && !dbFiles.length && !localFiles.length && selectedOption !== "file_query") {
      setSelectedOption(null);
    }
    filesSubmittedRef.current = false;
  };

  const currentFilesForPicker = useMemo(
    () => dbFiles.map((file) => ({ id: file.id, upload_type: file.upload_type, name: file.name })),
    [dbFiles]
  );

  if (!open && !isChatAiAnswerLoading) return null;

  const getPlaceholder = () => {
    if (loading) return "Loading...";
    if (selectedOption === "web_search") return "What do you want to search for?";
    if (selectedOption === "ai_questions") return "Ask anything about your file…";
    if (selectedOption === "file_query") return "Ask a question and AI will find the right file…";
    return "";
  };

  const showCustomPlaceholder =  !loading && !selectedOption && !inputValue;

  return (
    <Stack
      spacing={1}
      sx={{
        width: "100%",
        "@keyframes pulse": {
          "0%": {
            opacity: 1
          },
          "50%": {
            opacity: 0.6
          },
          "100%": {
            opacity: 1
          }
        }
      }}
    >
      {loading || (isChatAiAnswerLoading && <AiSkeletonLoader />)}
      {!isChatAiAnswerLoading && (
        <Stack
          sx={{
            position: "relative",
            margin: "9px 0px !important",
            borderRadius: "8px",
            border: loading ? "1px solid #071F35" : "1px solid #7B92A9",
            backgroundColor: "#fff",
            transition: "all 0.2s ease-in-out",
            ...(loading && {
              boxShadow: "0 0 0 1px rgba(7, 31, 53, 0.1)"
            })
          }}
        >
          <IconButton
            onClick={() => {
              if (loading || isTyping) return;
              onCloseAIBox();
            }}
            size="sm"
            sx={{
              backgroundColor: "#F5F6F7",
              position: "absolute",
              right: "-11px",
              top: "-11px",
              zIndex: 100,
              "&:hover": {
                backgroundColor: "#edeeee"
              }
            }}
          >
            <ClearTwoToneIcon fontSize="md" sx={{ fill: "#071F35" }} />
          </IconButton>
          <Box
            sx={{
              position: "absolute",
              left: "12px",
              top: 0,
              transform: "translateY(-50%)",
              color: "#496682",
              fontSize: "14px",
              fontWeight: 500,
              backgroundColor: "#fff",
              padding: "0 4px",
              height: "20px",
              pointerEvents: "none",
              zIndex: 1
            }}
          >
            Ask AI
          </Box>
          <Stack flexDirection="column" width="100%" sx={{ position: "relative" }}>
            {showCustomPlaceholder && (
              <Stack
                direction="row"
                alignItems="center"
                gap="4px"
                sx={{
                  position: "absolute",
                  top: "12px",
                  left: "12px",
                  right: "56px",
                }}
              >
                Ask anything. Attach a document with
                <AddIcon sx={{ fontSize: 18, color: "#071F35" }} />
                or search the web with
                <LanguageIcon sx={{ fontSize: 16, color: "#071F35" }} />
              </Stack>
            )}
            <Input
              fullWidth
              inputRef={inputRef}
              disabled={loading || isTyping}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text/plain");
                if (pastedText) {
                  setInputValue((prev) => prev + pastedText);
                }
              }}
              placeholder={getPlaceholder()}
              multiline
              maxRows={4}
              disableUnderline
              sx={{
                padding: "12px 12px 0px 12px",
                "& .MuiInput-input": {
                  padding: 0,
                  lineHeight: "24px",
                  minHeight: "40px",
                  "&::placeholder": {
                    color: "#7B92A9",
                    opacity: 1
                  },
                  ...(loading &&
                    inputValue && {
                      animation: "pulse 1.5s ease-in-out infinite"
                    })
                }
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={onGetAISuggestion}
                    disabled={loading || isTyping}
                    sx={{
                      backgroundColor: "#071F35 !important"
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          color: "#FFFFFF",
                          "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round"
                          }
                        }}
                      />
                    ) : (
                      <SendTo sx={{ fill: "#FFFFFF", width: "17px", height: "15px" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
            <Stack
              direction="column"
              spacing={1}
              sx={{
                padding: "0px 12px 6px 16px"
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "5px !important",
                  "& > *": {
                    margin: "0 !important"
                  }
                }}
              >
                {agentOptions.map((option, index) => {
                  const isBlocked =
                    selectedOption === "ai_questions" &&
                    option.value !== "ai_questions" &&
                    (!!dbFiles.length || !!localFiles.length);

                  return (
                    <MaterialTooltip
                      isShow
                      title={isBlocked ? "Remove selected files first" : option.label}
                      key={index}
                    >
                      <div>
                        <IconButton
                          onClick={() => handleOptionClick(option.value as IAskAiQueryType)}
                          disabled={isBlocked}
                          sx={{
                            backgroundColor:
                              selectedOption === option.value ||
                              (option.value === "ai_questions" && selectedOption === "file_query")
                                ? "#92A7BC !important"
                                : "#F5F6F7",
                            ...(option.value === "ai_questions" &&
                              selectedOption !== "ai_questions" &&
                              selectedOption !== "file_query" && {
                                bgcolor: "transparent"
                              }),
                            width: "26px",
                            height: "26px",
                            "&:hover": {
                              backgroundColor:
                                selectedOption === option.value ||
                                (option.value === "ai_questions" && selectedOption === "file_query")
                                  ? "#92A7BC"
                                  : "#ebeced"
                            },
                            "& svg": {
                              fill:
                                selectedOption === option.value ||
                                (option.value === "ai_questions" && selectedOption === "file_query")
                                  ? "#fff"
                                  : "#1C1B1F"
                            },
                            ...(isBlocked && {
                              opacity: 0.5,
                              cursor: "not-allowed",
                              "&:hover": {
                                backgroundColor: "#F5F6F7"
                              }
                            })
                          }}
                        >
                          {option.icon}
                        </IconButton>
                      </div>
                    </MaterialTooltip>
                  );
                })}
                {dbFiles.map((file, index) => (
                  <Chip
                    key={index}
                    onDelete={() => {
                      const remaining = dbFiles.filter((f) => f.id !== file.id);
                      setDbFiles(remaining);
                      if (!remaining.length && !localFiles.length) {
                        setSelectedOption(null);
                      }
                    }}
                    label={
                      <Typography
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        maxWidth="150px"
                        sx={{ fontSize: "12px", color: "#071F35" }}
                      >
                        {file.name}
                      </Typography>
                    }
                    deleteIcon={
                      <ClearTwoToneIcon
                        sx={{
                          width: "14px",
                          height: "14px",
                          fill: "#496682",
                          "&:hover": { backgroundColor: "#ebeced", borderRadius: "50%" }
                        }}
                      />
                    }
                    sx={{
                      height: "24px",
                      backgroundColor: "#DBE8F5",
                      borderRadius: "20px",
                      padding: "2px 8px",
                      gap: "5px",
                      maxWidth: "130px",
                      "& .MuiChip-label": {
                        px: "0px"
                      }
                    }}
                  />
                ))}
                {localFiles.map((file, index) => (
                  <Chip
                    key={index}
                    onDelete={() => {
                      const remaining = localFiles.filter((f) => f.size !== file.size || f.name !== file.name);
                      setLocalFiles(remaining);
                      if (!remaining.length && !dbFiles.length) {
                        setSelectedOption(null);
                      }
                    }}
                    label={
                      <Typography
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        maxWidth="150px"
                        sx={{ fontSize: "12px", color: "#071F35" }}
                      >
                        {file.name}
                      </Typography>
                    }
                    deleteIcon={
                      <ClearTwoToneIcon
                        sx={{
                          width: "14px",
                          height: "14px",
                          fill: "#496682",
                          "&:hover": { backgroundColor: "#ebeced", borderRadius: "50%" }
                        }}
                      />
                    }
                    sx={{
                      height: "26px",
                      backgroundColor: "#DBE8F5",
                      borderRadius: "20px",
                      padding: "2px 8px",
                      gap: "5px",
                      maxWidth: "130px",
                      "& .MuiChip-label": {
                        px: "0px"
                      }
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
      {isFilePickerOpen && (
        <FilesPickerModal
          currentFiles={currentFilesForPicker}
          attachedFiles={localFiles}
          filesPickerModalOpen={isFilePickerOpen}
          onClose={onCloseFilePicker}
          filesPickerSubmit={handleFilePickerSubmit}
          title="Add Files"
          workspaceTitle="Select or Upload Files"
          dealId={documentDealId}
          mode={FilesPickerMode.Document}
          getFilesOutsideDeal={!currentDealId}
          onSelectFileForMe={handleSelectFileForMe}
          isFileForMeSelected={selectedOption === "file_query"}
        />
      )}
    </Stack>
  );
};
